// Scale bridge dashboard - Ant Design theme tokens.
// Single source of truth for colours, spacing and typography. Mirrors the token
// structure SBIS's desktop uses, so the two dashboards read as one family.

import type { ThemeConfig } from 'antd';

export const BRAND = {
    name: 'Scale Bridge',
    tagline: 'Weighbridge label capture',

    primary: '#1d4ed8',       // indigo-700
    primaryHover: '#1e40af',  // indigo-800
    accent: '#0ea5e9',        // sky-500

    // ─── Status colours, chosen with the palette validator, not by eye ───
    // The obvious pair for pass/fail is green/red. Measured, #16a34a against
    // #dc2626 separates by only ΔE 5.0 under deuteranopia - below even the 6-8
    // floor - so a red/green reader cannot tell a passing test from a failing
    // one. Teal #0d9488 against the same red measures ΔE 13.1 and passes every
    // check, at no cost to how "pass" reads to everyone else.
    //
    // Colour is still never the only signal: StatusTag ships an icon and the
    // word itself alongside it.
    success: '#0d9488',       // teal-600 - validated against danger
    warning: '#f59e0b',
    danger: '#dc2626',

    sidebarBg: '#0f172a',     // slate-900
    sidebarHover: '#1e293b',  // slate-800
    sidebarText: '#cbd5e1',   // slate-300
    sidebarActive: '#3b82f6', // blue-500

    surface: '#f8fafc',       // slate-50 - page background
    /** The tone a row takes on hover, and the tint behind a table header. */
    surfaceHover: '#f1f5f9',  // slate-100
    ink: '#0f172a',           // slate-900 - body text
    border: '#e2e8f0',
    // Form controls need a darker outline than table rules; at #e2e8f0 a Select
    // reads as flat text.
    controlBorder: '#cbd5e1', // slate-300
    // Muted text. Slate-400 lands at 2.6:1 on white, under even the 3:1
    // large-text bar; slate-500 is 4.8:1. Against the navy sidebar the polarity
    // flips and slate-400 is correct there.
    muted: '#64748b',
    mutedOnDark: '#94a3b8',

    // The one hue every chart series uses. Validated: passes lightness band,
    // chroma floor and 3:1 contrast against the chart surface.
    chartSeries: '#1d4ed8',
    chartGrid: '#e2e8f0',
    chartAxis: '#64748b',
};

// ─── Light only, and deliberately so ───────────────────────────────────
// This runs on an operator's PC in a lit office, and a dark theme that is an
// automatic inversion of a light one is worse than no dark theme: the validated
// contrast figures above are measured against a light surface and do not carry
// over. A real dark mode means re-stepping every colour against the dark surface
// and re-running the validator, which is a deliberate piece of work, not a flag.
export const theme: ThemeConfig = {
    cssVar: true,
    token: {
        colorPrimary: BRAND.primary,
        colorSuccess: BRAND.success,
        colorWarning: BRAND.warning,
        colorError: BRAND.danger,
        colorInfo: BRAND.accent,
        colorBgLayout: BRAND.surface,
        colorBorder: BRAND.border,
        colorTextPlaceholder: BRAND.muted,
        colorText: BRAND.ink,
        colorTextSecondary: BRAND.muted,
        borderRadius: 8,
        fontFamily: `-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif`,
        fontSize: 14,
        // One heading scale, so a card title on Overview and a card title on
        // Devices are the same size. Left to its defaults, antd steps h5 down to
        // 16px and the Typography.Title used inside cards ends up competing with
        // the Card's own title at a different weight.
        fontSizeHeading4: 20,
        fontSizeHeading5: 15,
        lineHeight: 1.5715,
        wireframe: false,
    },
    components: {
        Layout: {
            headerBg: '#ffffff',
            headerHeight: 64,
            headerPadding: '0 24px',
            siderBg: BRAND.sidebarBg,
            bodyBg: BRAND.surface,
        },
        Menu: {
            darkItemBg: BRAND.sidebarBg,
            darkSubMenuItemBg: BRAND.sidebarBg,
            darkItemColor: BRAND.sidebarText,
            darkItemHoverBg: BRAND.sidebarHover,
            darkItemHoverColor: '#ffffff',
            darkItemSelectedBg: BRAND.primary,
            darkItemSelectedColor: '#ffffff',
            itemBorderRadius: 6,
            itemMarginInline: 8,
        },
        Card: {
            borderRadiusLG: 10,
            paddingLG: 20,
            headerFontSize: 15,
            headerHeight: 52,
            boxShadowTertiary: '0 1px 2px rgba(15, 23, 42, 0.04), 0 0 0 1px rgba(15, 23, 42, 0.04)',
        },
        Table: {
            headerBg: '#f8fafc',
            headerColor: '#475569',
            headerSplitColor: 'transparent',
            rowHoverBg: BRAND.surfaceHover,
            borderColor: BRAND.border,
            // The header is sticky on the long tables, so it has to read as a
            // header while rows slide under it rather than as one more row.
            headerBorderRadius: 0,
            cellPaddingBlock: 12,
            footerBg: '#ffffff',
        },
        Button: { controlHeight: 36, fontWeight: 500, colorBorder: BRAND.controlBorder },
        Input: { controlHeight: 36, colorBorder: BRAND.controlBorder },
        Select: { controlHeight: 36, colorBorder: BRAND.controlBorder },
        Statistic: { titleFontSize: 13 },
        Modal: { titleFontSize: 16 },
        // Descriptions carry the weighing detail and the connection facts. The
        // label column gets the page tint so the label/value split is legible
        // without leaning on the bordered variant's heavier rules.
        Descriptions: {
            labelBg: BRAND.surface,
            titleMarginBottom: 12,
            itemPaddingBottom: 12,
        },
        Drawer: { paddingLG: 24 },
        Tag: { defaultBg: BRAND.surface, defaultColor: BRAND.muted },
        Tooltip: { colorBgSpotlight: '#1e293b', borderRadius: 6 },
        Alert: { withDescriptionIconSize: 20 },
        List: { itemPaddingSM: '10px 0', itemPadding: '14px 0' },
        Segmented: { itemSelectedBg: '#ffffff', trackBg: BRAND.surfaceHover },
        Empty: { colorTextDescription: BRAND.muted },
    },
};
